import { TIME_SETTINGS } from "../constants/appConstants";

const SEC_PER_MIN = 60;
const SEC_PER_HOUR = 3600;
const SEC_PER_DAY = TIME_SETTINGS.CORPORATE_WORKING_HOURS * SEC_PER_HOUR;
const SEC_PER_WEEK = 7 * SEC_PER_DAY;
const SEC_PER_MONTH = 30 * SEC_PER_DAY;
const SEC_PER_YEAR = 365 * SEC_PER_DAY;

export const TIME_SPENT_PLACEHOLDER = "e.g. 2d · 1w · 2h · 30min · 1h 30min";
export const TIME_SPENT_HINT =
  "Accepted formats: 2d · 1w · 3m · 1y · 2h · 30min · 1h 30min";
export const TIME_SPENT_ERROR =
  "Enter a valid duration (e.g. 2d, 1w, 3m, 1y, 2h, 30min, 1h 30min).";

/**
 * Normalise user input before parsing (lowercase, collapse spaces, map `m` → `mon`).
 */
function normalizeTimeInput(rawInput) {
  return String(rawInput ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ")
    .replace(/(\d+)\s*m(?!in)/g, "$1 mon");
}

/**
 * Converts a time string (e.g. "2d", "1w", "3 mon", "30 min") into seconds.
 *
 * Conversion rates:
 * - min: 60 seconds
 * - h: 3600 seconds
 * - d: 1 working day (corporate hours)
 * - w: 7 working days
 * - mon: 30 working days
 * - y: 365 working days
 */
export function parseTimeSpentToSeconds(timeStr) {
  if (!timeStr || typeof timeStr !== "string") return 0;

  const normalized = normalizeTimeInput(timeStr);
  if (!normalized) return 0;

  const tokenPattern = /(\d+)\s*(min|h|d|w|mon|y)/gi;
  let totalSeconds = 0;
  let match;
  let matched = false;

  while ((match = tokenPattern.exec(normalized)) !== null) {
    matched = true;
    const value = parseInt(match[1], 10);
    const unit = match[2].toLowerCase();

    switch (unit) {
      case "min":
        totalSeconds += value * SEC_PER_MIN;
        break;
      case "h":
        totalSeconds += value * SEC_PER_HOUR;
        break;
      case "d":
        totalSeconds += value * SEC_PER_DAY;
        break;
      case "w":
        totalSeconds += value * SEC_PER_WEEK;
        break;
      case "mon":
        totalSeconds += value * SEC_PER_MONTH;
        break;
      case "y":
        totalSeconds += value * SEC_PER_YEAR;
        break;
      default:
        break;
    }
  }

  if (!matched) return 0;

  const remainder = normalized.replace(/(\d+)\s*(min|h|d|w|mon|y)/gi, "").replace(/\s+/g, "");
  if (remainder.length > 0) return 0;

  return totalSeconds;
}

/**
 * Formats seconds into a human-readable string (e.g. "1d 2h 30min").
 */
export function formatTimeSpent(totalSeconds) {
  if (totalSeconds == null || Number.isNaN(totalSeconds) || totalSeconds <= 0) {
    return "0min";
  }

  let remaining = Math.floor(totalSeconds);

  const years = Math.floor(remaining / SEC_PER_YEAR);
  remaining %= SEC_PER_YEAR;

  const months = Math.floor(remaining / SEC_PER_MONTH);
  remaining %= SEC_PER_MONTH;

  const weeks = Math.floor(remaining / SEC_PER_WEEK);
  remaining %= SEC_PER_WEEK;

  const days = Math.floor(remaining / SEC_PER_DAY);
  remaining %= SEC_PER_DAY;

  const hours = Math.floor(remaining / SEC_PER_HOUR);
  remaining %= SEC_PER_HOUR;

  const mins = Math.floor(remaining / SEC_PER_MIN);

  const parts = [];
  if (years) parts.push(`${years}y`);
  if (months) parts.push(`${months}mon`);
  if (weeks) parts.push(`${weeks}w`);
  if (days) parts.push(`${days}d`);
  if (hours) parts.push(`${hours}h`);
  if (mins) parts.push(`${mins}min`);

  return parts.length > 0 ? parts.join(" ") : "0min";
}

/** Read estimated time from a task payload as seconds. */
export function resolveEstimatedSeconds(task) {
  const value = task?.estimated_time ?? task?.estimatedTime;
  if (value == null || value === "") return 0;

  if (typeof value === "number") {
    return Number.isFinite(value) && value > 0 ? Math.floor(value) : 0;
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return 0;

    const numeric = Number(trimmed);
    if (Number.isFinite(numeric) && numeric > 0) {
      return Math.floor(numeric);
    }

    return parseTimeSpentToSeconds(trimmed);
  }

  return 0;
}

/** Validate a time-spent string and return parsed seconds when valid. */
export function validateTimeSpentInput(rawInput, { required = true } = {}) {
  const input = String(rawInput ?? "").trim();
  if (!input) {
    return required
      ? { valid: false, error: "Duration is required.", seconds: 0 }
      : { valid: true, error: null, seconds: 0 };
  }

  const seconds = parseTimeSpentToSeconds(input);
  if (seconds <= 0) {
    return { valid: false, error: TIME_SPENT_ERROR, seconds: 0 };
  }

  return { valid: true, error: null, seconds };
}
